import React from "react";

const PrivacyPage = () => {
  return (
    <div className="min-h-screen bg-white py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-slate-900 mb-4">Privacy Policy</h1>
          <p className="text-slate-600">Last Updated: July 24, 2025</p>
        </div>

        <div className="prose prose-slate max-w-none">
          <p className="text-lg text-slate-700 mb-8">
            Welcome to MyFinBrain. We are committed to protecting the privacy and security of the information of our website visitors, beta program participants, and clients. This Privacy Policy explains how MyFinBrain ("we," "us," or "our") collects, uses, discloses, and protects your information when you visit our website at myfinbrain.com (the "Website").
          </p>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">1. Information We Collect</h2>
            <p className="text-slate-700 mb-4">
              We collect information to provide and improve our services, communicate with you, and enhance your experience. The types of information we collect include:
            </p>
            
            <div className="mb-6">
              <h3 className="text-xl font-semibold text-slate-900 mb-3">Information You Provide Directly:</h3>
              <ul className="list-disc pl-6 space-y-2 text-slate-700">
                <li><strong>Contact Information:</strong> When you fill out forms on our Website (e.g., contact forms, beta program sign-up forms), you may provide your name, email address, phone number, company name, and job title.</li>
                <li><strong>Communications:</strong> Records of your correspondence with us, such as emails or support inquiries.</li>
              </ul>
            </div>

            <div className="mb-6">
              <h3 className="text-xl font-semibold text-slate-900 mb-3">Information Collected Automatically (Usage Data):</h3>
              <ul className="list-disc pl-6 space-y-2 text-slate-700">
                <li><strong>Technical Data:</strong> Your IP address, browser type and version, operating system, device type, unique device identifiers, and other technical information.</li>
                <li><strong>Usage Information:</strong> Pages you visit on our Website, time spent on those pages, links clicked, referral sources, and other Browse actions. This is often collected through cookies and similar tracking technologies.</li>
              </ul>
            </div>

            <div className="mb-6">
              <h3 className="text-xl font-semibold text-slate-900 mb-3">Information from Third Parties:</h3>
              <p className="text-slate-700">
                We may receive information about you from third-party services (e.g., analytics providers, marketing partners) to supplement the information you provide and enhance our understanding of your preferences.
              </p>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">2. How We Use Your Information</h2>
            <p className="text-slate-700 mb-4">
              We use the information we collect for various purposes, including:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-slate-700">
              <li><strong>To Provide and Maintain Our Services:</strong> Operating and maintaining our Website, processing beta program applications, and delivering any requested information or services.</li>
              <li><strong>To Improve Our Services:</strong> Understanding how our Website is used, identifying areas for improvement, and developing new features and offerings.</li>
              <li><strong>To Communicate With You:</strong> Responding to your inquiries, sending important notices (e.g., updates to this Privacy Policy), and providing marketing or promotional materials that may be of interest to you (with your consent where required).</li>
              <li><strong>For Security and Fraud Prevention:</strong> Detecting, preventing, and addressing technical issues, security incidents, and fraudulent activity.</li>
              <li><strong>For Legal Compliance:</strong> Fulfilling our legal obligations, resolving disputes, and enforcing our agreements.</li>
              <li><strong>For Research and Development:</strong> Analyzing trends and user behavior to refine our product and service offerings.</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">3. How We Share Your Information</h2>
            <p className="text-slate-700 mb-4">
              We may share your information in the following circumstances:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-slate-700">
              <li><strong>With Service Providers:</strong> We engage third-party companies and individuals to perform services on our behalf (e.g., website hosting, analytics, email delivery, CRM). These providers have access to your information only to perform these tasks and are obligated not to disclose or use it for any other purpose.</li>
              <li><strong>For Business Transfers:</strong> In connection with, or during negotiations of, any merger, sale of company assets, financing, or acquisition of all or a portion of our business by another company, your information may be transferred.</li>
              <li><strong>For Legal Reasons:</strong> We may disclose your information if required to do so by law or in response to valid requests by public authorities (e.g., a court or a government agency).</li>
              <li><strong>With Your Consent:</strong> We may share your information with third parties when we have your explicit consent to do so.</li>
              <li><strong>Aggregated or Anonymized Data:</strong> We may share aggregated or anonymized information that cannot reasonably be used to identify you.</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">4. Data Security</h2>
            <p className="text-slate-700">
              We implement reasonable technical and organizational measures to protect your information from unauthorized access, use, alteration, or destruction. However, no method of transmission over the Internet or method of electronic storage is 100% secure. Therefore, while we strive to use commercially acceptable means to protect your information, we cannot guarantee its absolute security.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">5. Data Retention</h2>
            <p className="text-slate-700">
              We retain your personal information only for as long as necessary to fulfill the purposes for which it was collected, including for the purposes of satisfying any legal, accounting, or reporting requirements. When your personal information is no longer needed, we will securely delete or anonymize it.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">6. Your Data Protection Rights</h2>
            <p className="text-slate-700 mb-4">
              Depending on your jurisdiction, you may have certain rights regarding your personal information. These rights may include:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-slate-700">
              <li><strong>Access:</strong> The right to request copies of your personal information.</li>
              <li><strong>Rectification:</strong> The right to request that we correct any information you believe is inaccurate or complete information you believe is incomplete.</li>
              <li><strong>Erasure:</strong> The right to request that we erase your personal information, under certain conditions.</li>
              <li><strong>Restriction of Processing:</strong> The right to request that we restrict the processing of your personal information, under certain conditions.</li>
              <li><strong>Object to Processing:</strong> The right to object to our processing of your personal information, under certain conditions.</li>
              <li><strong>Data Portability:</strong> The right to request that we transfer the data that we have collected to another organization, or directly to you, under certain conditions.</li>
              <li><strong>Withdraw Consent:</strong> Where we rely on your consent to process your personal information, you have the right to withdraw that consent at any time.</li>
            </ul>
            <p className="text-slate-700 mt-4">
              To exercise any of these rights, please contact us using the details provided below.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">7. Third-Party Links</h2>
            <p className="text-slate-700">
              Our Website may contain links to websites not operated by us. We have no control over and assume no responsibility for the content, privacy policies, or practices of any third-party sites or services. We encourage you to review the privacy policies of any third-party websites you visit.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">8. Children's Privacy</h2>
            <p className="text-slate-700">
              Our Website is not intended for individuals under the age of 18. We do not knowingly collect personally identifiable information from anyone under 18. If you are a parent or guardian and you are aware that your child has provided us with personal information, please contact us, and we will take steps to remove that information from our servers.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">9. Changes to This Privacy Policy</h2>
            <p className="text-slate-700">
              We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date at the top. We encourage you to review this Privacy Policy periodically for any changes.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">10. Contact Us</h2>
            <p className="text-slate-700 mb-4">
              If you have any questions about this Privacy Policy or our data practices, please contact us at:
            </p>
            <p className="text-slate-700">
              <strong>Email:</strong> <a href="mailto:hello@myfinbrain.com" className="text-blue-600 hover:text-blue-800">hello@myfinbrain.com</a>
            </p>
          </section>

        </div>
      </div>
    </div>
  );
};

export default PrivacyPage;
